/**
 * Task 151-B — shared RTL side-drawer primitive.
 *
 * A right-anchored (inline-start, RTL) panel over a dimming scrim, used
 * by the package create/edit flow. Follows the repo's existing inline-
 * styled `role="dialog"` overlay idiom (see ClientsListPage invite
 * modal) rather than introducing a portal/library. Adds drawer
 * affordances: Escape to close, scrim click-to-close, body scroll-lock,
 * a focus trap, and focus restore on unmount.
 *
 * Presentation only — no data, no API. The caller owns open/close state
 * (the drawer is mounted only while open) and provides the footer
 * actions.
 */

import { useCallback, useEffect, useId, useRef, type KeyboardEvent, type ReactNode } from 'react';

export function Drawer({
  title,
  eyebrow,
  onClose,
  children,
  footer,
  busy = false,
  width = 640,
}: {
  readonly title: string;
  readonly eyebrow?: string;
  readonly onClose: () => void;
  readonly children: ReactNode;
  readonly footer?: ReactNode;
  /** While busy (e.g. saving), scrim-click and Escape do not close. */
  readonly busy?: boolean;
  readonly width?: number;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  // Body scroll-lock + focus the panel on open; restore on unmount.
  useEffect(() => {
    const prevActive = document.activeElement as HTMLElement | null;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    panelRef.current?.focus();
    return () => {
      document.body.style.overflow = prevOverflow;
      prevActive?.focus?.();
    };
  }, []);

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Escape') {
        if (!busy) {
          e.stopPropagation();
          onClose();
        }
        return;
      }
      if (e.key !== 'Tab') return;
      const panel = panelRef.current;
      if (!panel) return;
      const focusables = panel.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      if (e.shiftKey) {
        if (active === first || active === panel) {
          e.preventDefault();
          last.focus();
        }
      } else if (active === last) {
        e.preventDefault();
        first.focus();
      }
    },
    [busy, onClose],
  );

  return (
    <div
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(20, 16, 10, 0.4)',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'flex-start',
        zIndex: 60,
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onKeyDown={onKeyDown}
        style={{
          width: `min(${width}px, 100%)`,
          height: '100%',
          background: 'var(--paper)',
          borderInlineStart: '1px solid var(--line)',
          boxShadow: '0 0 40px rgba(20,16,10,0.18)',
          display: 'flex',
          flexDirection: 'column',
          outline: 'none',
        }}
      >
        {/* Head */}
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 12,
            padding: '18px 22px',
            borderBottom: '1px solid var(--line)',
          }}
        >
          <div>
            {eyebrow ? (
              <div className="lbl" style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 2 }}>
                {eyebrow}
              </div>
            ) : null}
            <h2 id={titleId} style={{ fontSize: 20, margin: 0 }}>
              {title}
            </h2>
          </div>
          <button
            type="button"
            className="btn-text"
            onClick={onClose}
            disabled={busy}
            aria-label="إغلاق"
            style={{ padding: '6px 10px', fontSize: 18, lineHeight: 1 }}
          >
            ×
          </button>
        </div>

        {/* Body (scrollable) */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 22 }}>{children}</div>

        {/* Foot */}
        {footer ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 10,
              padding: '14px 22px',
              borderTop: '1px solid var(--line)',
              background: 'var(--cream-2)',
            }}
          >
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );
}
